import { useEffect, useMemo, useState } from "react";
import { AlertOctagon, BarChart3, Clock, X, Download } from "lucide-react";
import { api, formatApiError, API_BASE } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";

const ACCENT = "#EC4899";

// Color palette per Item category (stable, color-blind friendly)
const CATEGORY_COLORS = {
  "CM - Mold Shop":         "#F87171", // red
  "D - Mold Setup":         "#FB923C", // orange
  "CF - Fiberglass Shop":   "#FACC15", // yellow
  "A - Project Management": "#38BDF8", // sky
  "Q - Production Delay":   "#A78BFA", // violet
  "E - Spray":              "#34D399", // emerald
};
const FALLBACK = "#94A3B8";
const colorFor = (cat) => CATEGORY_COLORS[cat] || FALLBACK;

function fmtH(n) {
  if (n === null || n === undefined || isNaN(n)) return "0h";
  return `${Number(n).toFixed(1)}h`;
}

export default function MoldNcrReportCard({ showMexico = false, dept = 'MOLD' }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ groups: [], total_ncrs: 0, total_hours: 0 });
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("count"); // "count" | "hours"
  const [drill, setDrill] = useState(null);      // {problem, item} | null
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState("");

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (selectedJob) { p.job = selectedJob; }
    return p;
  }, [filter, showMexico, selectedJob]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept}/ncr-report`, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  // Fetch drill rows when drill changes
  useEffect(() => {
    if (!drill) { setDrillData(null); return; }
    let cancelled = false;
    setDrillLoading(true);
    setDrillData(null);
    api.get(`/departments/${dept}/ncr-report/rows`, {
      params: { ...params, problem: drill.problem, item: drill.item },
    })
      .then((r) => !cancelled && setDrillData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setDrillLoading(false));
    return () => { cancelled = true; };
  }, [drill, params]);

  // Escape key closes drawer
  useEffect(() => {
    if (!drill) return;
    const onKey = (e) => { if (e.key === "Escape") setDrill(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill]);

  // Flatten groups → per-problem rows with parent category
  const problems = useMemo(() => {
    const out = [];
    (data.groups || []).forEach((g) => {
      const hoursPerProblem =
        g.problems.reduce((sum, p) => sum + p.count, 0) > 0
          ? g.total_hours / g.problems.reduce((sum, p) => sum + p.count, 0)
          : 0;
      g.problems.forEach((p) => {
        out.push({
          label:    p.label,
          item:     g.item,
          count:    p.count,
          // Approximate per-problem hours by distributing the group's total hours
          // proportionally to the problem's count.
          hours:    +(hoursPerProblem * p.count).toFixed(1),
        });
      });
    });
    return out;
  }, [data]);

  const sorted = useMemo(() => {
    const key = metric === "hours" ? "hours" : "count";
    return [...problems].sort((a, b) => (b[key] || 0) - (a[key] || 0));
  }, [problems, metric]);

  // Category roll-up for the side panel
  const byCategory = useMemo(() => {
    const m = {};
    (data.groups || []).forEach((g) => {
      m[g.item] = { item: g.item, count: g.count, hours: g.total_hours };
    });
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [data]);

  const maxVal = sorted.reduce((m, p) => Math.max(m, p[metric] || 0), 0);

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid="mold-ncr-report-card"
    >
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertOctagon size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">NCR Report</h3>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
          Plant: {dept === "FRAME"
            ? (showMexico ? "FRAME-MEX" : "FRAME-SLC")
            : dept === "GFRC"
            ? (showMexico ? "GFRG-MEXICO / MXUD" : "GFRG-SLC / GFRC-MOLDS")
            : (showMexico ? "MOLDS-MEX" : "MOLDS-SLC")}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Top defect problems — Pareto view for team leads
      </p>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <DateFilter value={filter} onChange={setFilter} />
          <div className="inline-flex items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono">Job</span>
            <select
              data-testid="ncr-job-filter"
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="bg-[#0A0B10] border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-white cursor-pointer focus:outline-none focus:border-[#EC4899] transition-colors"
              title="Filter NCR report by job"
            >
              <option value="">All Jobs</option>
              {(data.jobs || []).map((j) => (
                <option key={j} value={j}>{j}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="inline-flex border border-border" data-testid="ncr-metric-toggle">
          <button
            data-testid="ncr-metric-count"
            onClick={() => setMetric("count")}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
              metric === "count" ? "bg-[#EC4899] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
            }`}
          >
            <BarChart3 size={11} /> By Count
          </button>
          <button
            data-testid="ncr-metric-hours"
            onClick={() => setMetric("hours")}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
              metric === "hours" ? "bg-[#EC4899] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
            }`}
          >
            <Clock size={11} /> By Hours
          </button>
        </div>
      </div>

      {/* Summary banner */}
      <div className="border border-red-700/40 bg-red-950/40 px-5 py-4 mb-5 flex flex-wrap items-center gap-6"
           data-testid="ncr-summary-banner">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 border border-red-700/50 flex items-center justify-center shrink-0">
            <AlertOctagon size={20} className="text-red-400" />
          </div>
          <div>
            <div className="font-heading font-bold text-2xl text-red-400 tabular-nums">
              {loading ? "—" : `${data.total_ncrs} NCRs`}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
              {loading ? "" : `${fmtH(data.total_hours)} field hours`}
            </div>
          </div>
        </div>
        {/* Inline category roll-up chips for quick read */}
        <div className="flex flex-wrap gap-2 ml-auto" data-testid="ncr-category-chips">
          {byCategory.map((c, i) => (
            <div key={`${c.item || "blank"}-${i}`}
                 className="flex items-center gap-2 px-2.5 py-1 border bg-[#0A0B10]/60"
                 style={{ borderColor: `${colorFor(c.item)}55` }}>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorFor(c.item) }} />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.item}</span>
              <span className="font-mono text-[11px] text-white">{c.count}</span>
              <span className="font-mono text-[10px] text-muted-foreground">· {fmtH(c.hours)}</span>
            </div>
          ))}
        </div>
      </div>

      {!loading && problems.length === 0 && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="ncr-empty">
          No NCRs in range
        </div>
      )}
      {loading && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      )}

      {!loading && problems.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              {metric === "count" ? "Top Defects by Count" : "Top Defects by Field Hours"}
            </div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">
              {sorted.length} problems · click a bar to drill
            </div>
          </div>

          <div
            data-testid="ncr-chart"
            style={{ height: `${Math.max(220, sorted.length * 36 + 24)}px` }}
          >
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart
                data={sorted}
                layout="vertical"
                margin={{ top: 5, right: 80, left: 16, bottom: 5 }}
              >
                <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#94A3B8" tick={{ fontSize: 10 }}
                  domain={[0, Math.ceil((maxVal || 1) * 1.15)]}
                />
                <YAxis
                  type="category" dataKey="label" stroke="#94A3B8"
                  tick={{ fontSize: 10 }} width={210}
                  interval={0}
                />
                <Tooltip
                  cursor={{ fill: "rgba(236,72,153,0.08)" }}
                  contentStyle={{ backgroundColor: "#0A0B10", border: "1px solid #1F2937", fontSize: 12 }}
                  labelStyle={{ color: "#E2E8F0" }}
                  formatter={(v, _k, ctx) => {
                    const row = ctx?.payload || {};
                    if (metric === "count") {
                      return [`${v} NCRs · ${fmtH(row.hours)}`, row.item || ""];
                    }
                    return [`${fmtH(v)} · ${row.count} NCRs`, row.item || ""];
                  }}
                  labelFormatter={(label) => label}
                />
                <Bar
                  dataKey={metric} barSize={20} radius={[0, 2, 2, 0]}
                  onClick={(d) => d && setDrill({ problem: d.label, item: d.item })}
                  cursor="pointer"
                >
                  {sorted.map((row) => (
                    <Cell key={row.label} fill={colorFor(row.item)} />
                  ))}
                  <LabelList
                    dataKey={metric}
                    position="right"
                    formatter={(v) => (metric === "count" ? v : fmtH(v))}
                    style={{ fill: "#E2E8F0", fontSize: 11, fontFamily: "monospace" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend (categories) */}
          <div className="mt-4 flex flex-wrap gap-3" data-testid="ncr-legend">
            {byCategory.map((c, i) => (
              <div key={`${c.item || "blank"}-${i}`} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                <span className="h-2.5 w-2.5" style={{ backgroundColor: colorFor(c.item) }} />
                {c.item}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Drill-down drawer */}
      {drill && (
        <NcrDrillDrawer
          drill={drill}
          data={drillData}
          loading={drillLoading}
          onClose={() => setDrill(null)}
          exportParams={params}
          dept={dept}
        />
      )}
    </section>
  );
}

function NcrDrillDrawer({ drill, data, loading, onClose, exportParams, dept = "MOLD" }) {
  const color = CATEGORY_COLORS[drill.item] || FALLBACK;

  const buildExportUrl = (fmt) => {
    const token = localStorage.getItem("mfg_token") || "";
    const qs = new URLSearchParams({
      fmt,
      problem: drill.problem || "",
      preset: exportParams?.preset || "all",
      show_mexico: String(!!exportParams?.show_mexico),
      token,
    });
    if (drill.item) qs.set("item", drill.item);
    if (exportParams?.start) qs.set("start", exportParams.start);
    if (exportParams?.end)   qs.set("end",   exportParams.end);
    return `${API_BASE}/departments/${dept}/ncr-report/export?${qs.toString()}`;
  };

  const hasRows = !loading && data && data.rows && data.rows.length > 0;

  const triggerDownload = (fmt) => {
    if (!hasRows) { toast.error("Nothing to export"); return; }
    window.location.href = buildExportUrl(fmt);
    toast.success(`Exporting ${data.rows.length} rows as ${fmt.toUpperCase()}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-end p-0 sm:p-4 lg:pl-[17rem] overflow-y-auto"
      onClick={onClose}
      data-testid="ncr-drill-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-3xl bg-[#0E0F17] border border-border min-h-full sm:min-h-0 sm:max-h-[90vh] flex flex-col"
      >
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3"
             style={{ background: `linear-gradient(180deg, ${color}1A 0%, transparent 100%)` }}>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              {drill.item || "NCR DRILL"}
            </div>
            <h4 className="font-heading font-bold text-xl mt-1 truncate" title={drill.problem}>
              {drill.problem}
            </h4>
            {data && (
              <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                {data.count} NCRs · {fmtH(data.total_hours)} field hours · Plant {data.plant}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); triggerDownload("csv"); }}
              disabled={!hasRows}
              data-testid="ncr-drill-export"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border text-[10px] tracking-widest uppercase hover:border-[#EC4899]/60 hover:text-white transition-colors disabled:opacity-40 disabled:hover:border-border disabled:hover:text-current"
              title="Download these rows as CSV"
            >
              <Download size={12} />
              CSV
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); triggerDownload("pdf"); }}
              disabled={!hasRows}
              data-testid="ncr-drill-export-pdf"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border text-[10px] tracking-widest uppercase hover:border-[#EC4899]/60 hover:text-white transition-colors disabled:opacity-40 disabled:hover:border-border disabled:hover:text-current"
              title="Download these rows as PDF"
            >
              <Download size={12} />
              PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="ncr-drill-close"
              className="p-1.5 border border-border hover:border-[#EC4899]/60 transition-colors"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto" data-testid="ncr-drill-body">
          {loading && (
            <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
              Loading…
            </div>
          )}
          {!loading && data && data.rows.length === 0 && (
            <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
              No NCR rows
            </div>
          )}
          {!loading && data && data.rows.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-[#1A1C28] sticky top-0 text-[10px] uppercase tracking-widest text-[#94A3B8]">
                <tr>
                  <th className="py-2 px-3 text-left">Date</th>
                  <th className="py-2 px-3 text-left">Job</th>
                  <th className="py-2 px-3 text-left">Piece Mark</th>
                  <th className="py-2 px-3 text-left">Piece S/N</th>
                  <th className="py-2 px-3 text-left">Cause</th>
                  <th className="py-2 px-3 text-left">Status</th>
                  <th className="py-2 px-3 text-right">Field Hrs</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-t border-border/30 hover:bg-[#1A1C28]/40">
                    <td className="py-2 px-3 font-mono text-muted-foreground">
                      {(r.date || "").slice(0, 10) || "—"}
                    </td>
                    <td className="py-2 px-3 max-w-[180px] truncate" title={r.job}>{r.job || "—"}</td>
                    <td className="py-2 px-3 font-mono">{r.piece_mark || "—"}</td>
                    <td className="py-2 px-3 font-mono text-muted-foreground">{r.piece_sn || "—"}</td>
                    <td className="py-2 px-3">{r.cause || "—"}</td>
                    <td className="py-2 px-3">
                      <span className="inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 border border-border bg-[#0A0B10] text-[#CBD5E1]">
                        {r.status || "—"}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-right text-red-400">
                      {r.field_hours ? fmtH(r.field_hours) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
